import { useState, useCallback } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Alert,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import QuizIcon from '@mui/icons-material/Quiz'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMemorization } from '../hooks/useMemorization'
import { useAuth } from '../hooks/useAuth'
import { useOnlineStatus } from '../hooks/useOnlineStatus'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { MemorizationOnboarding } from '../components/MemorizationOnboarding'
import { MemorizationEmptyState } from '../components/MemorizationEmptyState'
import { OfflineNotice } from '../components/OfflineNotice'
import { profileApi } from '../lib/api'

/** True when nextReviewDate is today or earlier (UTC date compare). */
function isReviewDue(nextReviewDate?: string): boolean {
  if (!nextReviewDate) return false
  const todayUtc = new Date().toISOString().slice(0, 10)
  return nextReviewDate.slice(0, 10) <= todayUtc
}

export default function LearnVerses() {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const { t, i18n } = useTranslation()
  const { user, refreshUser } = useAuth()
  // Local override to force-show the full guide even after the user has
  // completed/dismissed it ("Show guide again" link in the compact state).
  const [forceShowGuide, setForceShowGuide] = useState(false)

  const { verses, loading, error, loadVerses, removeVerse } = useMemorization({ status: statusFilter })
  const online = useOnlineStatus()
  // Pending-delete dialog state: holds the verse the user tapped delete on,
  // null when no dialog is open. Confirming calls the hook's optimistic
  // removeVerse (rolls back on API failure).
  const [pendingDelete, setPendingDelete] = useState<{ surahNumber: number; ayahNumber: number } | null>(null)
  const [removing, setRemoving] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)

  const confirmRemove = useCallback(async () => {
    if (!pendingDelete) return
    setRemoving(true)
    setRemoveError(null)
    try {
      await removeVerse(pendingDelete.surahNumber, pendingDelete.ayahNumber)
      setPendingDelete(null)
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Failed to remove verse')
    } finally {
      setRemoving(false)
    }
  }, [pendingDelete, removeVerse])

  const onboardingComplete = Boolean(user?.onboardingCompleted?.memorization)
  const handleDismissOnboarding = useCallback(async () => {
    try {
      await profileApi.markOnboardingCompleted('memorization')
      void refreshUser()
    } catch {
      // Best-effort — if the network blip swallows it, the user can dismiss
      // again on next visit. No need to surface a toast for an in-progress
      // empty state.
    }
  }, [refreshUser])

  if (loading) {
    return <LoadingSpinner message={t('memorization.loading')} />
  }

  return (
    <Box>
      {error && !online ? (
        <OfflineNotice onRetry={loadVerses} />
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {verses.length > 0 && (
        <Alert severity="info" icon={false} sx={{ mb: 2 }}>
          <Typography variant="body2">{t('memorization.reviewHint')}</Typography>
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Chip
          label={t('memorization.filterAll')}
          variant={!statusFilter ? 'filled' : 'outlined'}
          onClick={() => setStatusFilter(undefined)}
        />
        <Chip
          label={t('memorization.filterLearning')}
          variant={statusFilter === 'learning' ? 'filled' : 'outlined'}
          onClick={() => setStatusFilter('learning')}
        />
        <Chip
          label={t('memorization.filterReviewing')}
          variant={statusFilter === 'reviewing' ? 'filled' : 'outlined'}
          onClick={() => setStatusFilter('reviewing')}
        />
        <Chip
          label={t('memorization.filterMastered')}
          variant={statusFilter === 'mastered' ? 'filled' : 'outlined'}
          onClick={() => setStatusFilter('mastered')}
        />
      </Stack>

      {verses.length === 0 ? (
        // Empty state. We show the full step-by-step onboarding only for
        // users who haven't completed/dismissed it yet (or who explicitly
        // asked to see it again). Returning users in a temporary empty
        // state get the lighter MemorizationEmptyState.
        (!onboardingComplete || forceShowGuide) ? (
          <MemorizationOnboarding
            primaryCtaPath="/reader"
            onDismiss={() => {
              setForceShowGuide(false)
              void handleDismissOnboarding()
            }}
          />
        ) : (
          <MemorizationEmptyState
            primaryCtaPath="/reader"
            onShowGuide={() => setForceShowGuide(true)}
          />
        )
      ) : (
        <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
          {verses.map((verse) => {
            const due = isReviewDue(verse.nextReviewDate)
            return (
            <Grid
              size={{ xs: 12, sm: 6, md: 4 }}
              key={`${verse.surahNumber}-${verse.ayahNumber}`}
              sx={{ display: 'flex' }}
            >
              <Card sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('memorization.surahAyah', { surah: verse.surahNumber, ayah: verse.ayahNumber })}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                      <Chip
                        label={t(`memorization.status${verse.status.charAt(0).toUpperCase()}${verse.status.slice(1)}`)}
                        size="small"
                        color={verse.status === 'mastered' ? 'success' : verse.status === 'reviewing' ? 'info' : 'warning'}
                        variant={verse.status === 'mastered' ? 'filled' : 'outlined'}
                      />
                      <Tooltip title={t('memorization.removeVerse')}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setPendingDelete({
                              surahNumber: verse.surahNumber,
                              ayahNumber: verse.ayahNumber,
                            })
                          }
                          aria-label={t('memorization.removeVerse')}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Typography variant="body2">{t('memorization.confidence', { value: verse.confidence })}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('memorization.reviewsAndSuccess', {
                      reviews: verse.reviewCount,
                      success: Math.round(verse.successRate),
                    })}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: due ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    {due
                      ? t('memorization.dueToday')
                      : t('memorization.nextReview', {
                          date: verse.nextReviewDate
                            ? new Date(verse.nextReviewDate).toLocaleDateString(i18n.language)
                            : t('memorization.noNextReview'),
                        })}
                  </Typography>
                  <Box sx={{ mt: 'auto', pt: 1 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<QuizIcon />}
                      onClick={() =>
                        navigate('/games/multiple-choice', {
                          state: { source: 'memorization' },
                        })
                      }
                    >
                      {t('memorization.practiceVerse')}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            )
          })}
        </Grid>
      )}

      {/* Confirm remove dialog. Opened by the per-card trash icon. Wording
          spells out the SRS-progress loss because removing isn't reversible
          beyond re-adding (the new row resets reviewCount/successRate). */}
      <Dialog
        open={pendingDelete !== null}
        onClose={() => (removing ? undefined : setPendingDelete(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('memorization.removeConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingDelete &&
              t('memorization.removeConfirmBody', {
                surah: pendingDelete.surahNumber,
                ayah: pendingDelete.ayahNumber,
              })}
          </DialogContentText>
          {removeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {removeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={removing}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmRemove} color="error" variant="contained" disabled={removing}>
            {removing ? <CircularProgress size={20} color="inherit" /> : t('common.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

